import { useLocation } from '#app/hooks/useLocation.js';
import { logger } from '#app/services/logger.js';
import { useMemo } from 'react';
// This is the only place where we allow the usage of vike/client/router
// eslint-disable-next-line canonical/no-restricted-imports
import { navigate } from 'vike/client/router';

const VITEST =
  // eslint-disable-next-line n/no-process-env
  typeof process === 'undefined' ? false : process?.env['VITEST'] === 'true';

export const useNavigation = () => {
  const location = useLocation();

  return useMemo(() => {
    return {
      back: () => {
        if (VITEST) {
          // eslint-disable-next-line no-console
          console.warn('navigation is ignored in unit tests');

          return;
        }

        logger.info(
          {
            namespace: 'navigation',
          },
          'back',
        );

        // TODO investigate why navigating to the lastLocation does not achieve equivalent result
        // eslint-disable-next-line no-restricted-properties
        window.history.back();
      },
      push: (path: string, options: { keepScrollPosition?: boolean } = {}) => {
        if (VITEST) {
          // eslint-disable-next-line no-console
          console.warn('navigation is ignored in unit tests', { path });

          return;
        }

        if (location.pathname + location.search === path) {
          logger.error(
            {
              namespace: 'navigation',
            },
            'ignoring an attempt to navigate to the same location',
          );

          return;
        }

        logger.info(
          {
            namespace: 'navigation',
            path,
          },
          'push',
        );

        if (path.startsWith('http://') || path.startsWith('https://')) {
          // eslint-disable-next-line no-restricted-properties
          window.location.assign(path);
        } else {
          const { keepScrollPosition } = options;

          if (!path.startsWith('/')) {
            throw new Error(`invalid path: ${path}; must start with "/"`);
          }

          // @ts-expect-error TODO
          void navigate(path, {
            keepScrollPosition: keepScrollPosition ?? true,
            overwriteLastHistoryEntry: false,
          });
        }
      },
      reload: () => {
        if (VITEST) {
          // eslint-disable-next-line no-console
          console.warn('navigation is ignored in unit tests');

          return;
        }

        logger.info(
          {
            namespace: 'navigation',
          },
          'reload',
        );

        const path = location.path;

        if (!path.startsWith('/')) {
          throw new Error(`invalid path: ${path}; must start with "/"`);
        }

        // TODO investigate why "reload" from vike/client/router doesn't work
        // TODO navigate doesn't work either so resorting to
        // window.location.reload to get this functionality working
        // void navigate(path, {
        //   keepScrollPosition: true,
        //   overwriteLastHistoryEntry: true,
        // });

        // eslint-disable-next-line no-restricted-properties
        window.location.reload();
      },
      replace: (
        path: string,
        options: { keepScrollPosition?: boolean } = {},
      ) => {
        if (VITEST) {
          // eslint-disable-next-line no-console
          console.warn('navigation is ignored in unit tests', { path });

          return;
        }

        if (location.pathname + location.search === path) {
          logger.error(
            {
              namespace: 'navigation',
            },
            'ignoring an attempt to navigate to the same location',
          );

          return;
        }

        logger.info(
          {
            namespace: 'navigation',
            path,
          },
          'replace',
        );

        if (path.startsWith('http://') || path.startsWith('https://')) {
          // eslint-disable-next-line no-restricted-properties
          window.location.replace(path);
        } else {
          const { keepScrollPosition } = options;

          if (!path.startsWith('/')) {
            throw new Error(`invalid path: ${path}; must start with "/"`);
          }

          // @ts-expect-error TODO
          void navigate(path, {
            keepScrollPosition: keepScrollPosition ?? false,
            overwriteLastHistoryEntry: true,
          });
        }
      },
    };
  }, [location]);
};
